import React from 'react'
import type { Flow } from '../../../../shared/domain/flows/types.no-deps'
import type { FlowGroup, FlowGroupUnit } from '../../../../shared/domain/viewableReports/types.no-deps'
import { interleaveWith, last } from '../../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../../shared/lib/functional/pipe.no-deps'
import type { Nullable } from '../../../../shared/lib/typeHelpers.no-deps'
import { ViewFlowItems } from './ViewFlowItems'
import { GroupWrapper } from './GroupWrapper'
import type { SharedProps } from './SharedProps'
import { type TopLevelStepGrouping, getSectionGroupings } from './getSectionGroupings'
import { getDataForUnit, type CachedUnitData } from '../../../../shared/domain/units/CachedUnitData.no-deps'
import type { UnitData } from '../../../../shared/domain/units/types.no-deps'
import { fromLoopPath, type LoopPath } from '../../../../shared/domain/flows/LoopPath.no-deps'
import { isNotNil } from '../../../../shared/lib/functional/nil.no-deps'

interface Props extends SharedProps {
    flowGroups: FlowGroup[]
    flow: Nullable<Flow>
    cachedUnitData: CachedUnitData
    subjectUnitData?: UnitData
}

export class ViewFlowGroups extends React.Component<Props> {
    private findUnitData(loopPath: LoopPath) {
        const { subjectUnitData, cachedUnitData } = this.props
        const loopId = last(fromLoopPath(loopPath))
        return isNotNil(loopId) ? getDataForUnit(cachedUnitData, loopId.unitId) : subjectUnitData
    }

    private renderUnstructuredAnswers = () => {
        const { flowGroups, deps, renderCachedImage } = this.props

        // There is no id on a flowGroup so we are using the index here
        // It should be okay since these should never change order.
        // In future we could add an id to the flow groups.
        return flowGroups.map((flowGroup, index) => (
            <GroupWrapper key={index}>
                {flowGroup.units.length > 0 && (
                    <div className="AppCore__report__FlowGroup__Units">
                        {pipe(
                            flowGroup.units.map((unit) => {
                                return (
                                    <span key={unit.id} className="AppCore__report__FlowGroup__Unit">
                                        {renderCachedImage(
                                            unit.image,
                                            'AppCore__report__FlowGroup__UnitImage',
                                            deps.getImage('missing-unit')
                                        )}
                                        <span className="AppCore__report__FlowGroup__UnitName">{unit.name}</span>
                                    </span>
                                )
                            }),
                            interleaveWith(<span>/</span>)
                        )}
                    </div>
                )}
                <ViewFlowItems
                    {...this.props}
                    items={flowGroup.items}
                    unitData={this.findUnitData(flowGroup.loopPath)}
                />
            </GroupWrapper>
        ))
    }

    private renderSectionTitle = (labels: string[]) => {
        if (labels.length === 0) return
        if (labels.length === 1) return <div className="AppCore__report__FlowGroup__SectionTitle">{labels[0]}</div>
        const label = labels[labels.length - 1]
        const parentLabels = labels.slice(0, -1)
        return (
            <div className="AppCore__report__FlowGroup__SectionTitle">
                <span className="AppCore__report__FlowGroup__SectionTitle--parents">{parentLabels.join(' / ')}</span>
                <span> / {label}</span>
            </div>
        )
    }

    private renderLoopAnswers = (stepGroupings: TopLevelStepGrouping[], units: FlowGroupUnit[], key: number) => {
        return (
            <div key={key} className="AppCore__report__FlowGroup__StepGrouping">
                <div className="AppCore__report__FlowGroup__Loop">
                    {units.map((unit) => (
                        <div key={unit.id} className="AppCore__report__FlowGroup__LoopUnit">
                            <zaptic-report-subject
                                unit-name={unit.name}
                                image-url={unit.image}
                                language={this.props.deps.language}
                                compact
                            />
                        </div>
                    ))}
                    <div className="AppCore__report__FlowGroup__LoopSteps">
                        {stepGroupings.map((stepGrouping) => (
                            <div key={stepGrouping.id} className="AppCore__report__FlowGroup__LoopStep">
                                <div className="AppCore__report__FlowGroup__StepTitle">{stepGrouping.label}</div>
                                <ViewFlowItems
                                    {...this.props}
                                    items={stepGrouping.items}
                                    unitData={this.findUnitData(stepGrouping.loopPath)}
                                />
                                {stepGrouping.sectionGroupings.map((sectionGrouping) => (
                                    <div key={sectionGrouping.id} className="AppCore__report__FlowGroup__Section">
                                        {this.renderSectionTitle(sectionGrouping.labels)}
                                        <div>
                                            {sectionGrouping.steps.map((sectionStep) => (
                                                <div key={sectionStep.id}>
                                                    <div className="AppCore__report__FlowGroup__StepTitle">
                                                        {sectionStep.label}
                                                    </div>
                                                    <div>
                                                        <ViewFlowItems
                                                            {...this.props}
                                                            items={sectionStep.items}
                                                            unitData={this.findUnitData(sectionGrouping.loopPath)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    public render() {
        const { flow, flowGroups } = this.props

        if (!flow) return this.renderUnstructuredAnswers()

        const sectionGroupings = flowGroups.map((flowGroup) => {
            const { stepGroupings, remainingItems } = getSectionGroupings(
                flow.steps,
                flowGroup.items,
                flowGroup.loopPath
            )

            return {
                stepGroupings,
                remainingItems,
                units: flowGroup.units,
            }
        })

        if (sectionGroupings.some(({ remainingItems }) => remainingItems.length !== 0)) {
            return this.renderUnstructuredAnswers()
        }

        return sectionGroupings.map((sectionGrouping, index) => {
            if (sectionGrouping.units.length > 0) {
                return this.renderLoopAnswers(sectionGrouping.stepGroupings, sectionGrouping.units, index)
            }

            // There is no id on a flowGroup so we are using the index here
            // It should be okay since these should never change order.
            // In future we could add an id to the flow groups.
            return (
                <div key={index} className="AppCore__report__FlowGroup__StepGrouping">
                    {sectionGrouping.stepGroupings.map((stepGrouping) => (
                        <div key={stepGrouping.id} className="AppCore__report__FlowGroup__Step">
                            <div className="AppCore__report__FlowGroup__StepTitle">{stepGrouping.label}</div>
                            <ViewFlowItems
                                {...this.props}
                                items={stepGrouping.items}
                                unitData={this.findUnitData(stepGrouping.loopPath)}
                            />
                            {stepGrouping.sectionGroupings.map((sectionGrouping) => (
                                <div key={sectionGrouping.id} className="AppCore__report__FlowGroup__Section">
                                    {this.renderSectionTitle(sectionGrouping.labels)}
                                    <div>
                                        {sectionGrouping.steps.map((sectionStep) => (
                                            <div
                                                key={sectionStep.id}
                                                className="AppCore__report__FlowGroup__SectionStep"
                                            >
                                                <div className="AppCore__report__FlowGroup__StepTitle">
                                                    {sectionStep.label}
                                                </div>
                                                <div>
                                                    <ViewFlowItems
                                                        {...this.props}
                                                        items={sectionStep.items}
                                                        unitData={this.findUnitData(sectionGrouping.loopPath)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )
        })
    }
}
